import { useEffect, useState } from "react";
import { Accordion, Badge, Card, Col, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import { useSession } from "../../contexts/SessionContext";
import { EphemeralUserModel } from "../../models/EphemeralUser";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { fetchEphemeralUserById } from "./EphemeralUser.function";
import "./EphemeralUser.scss";

const _getStatusColor = (succeed?: boolean | null) =>
  succeed === true ? "success" : succeed === false ? "danger" : "secondary";

const _getUserScore = (user: EphemeralUserModel) => {
  const answers = user.answers ?? [];
  const total = answers.length;
  const success = answers.filter((answer) => answer.succeed).length;
  const failed = total - success;
  const ratio = total ? Math.round((success / total) * 100) : 0;

  return { success, failed, total, ratio };
};

const _getUserDuration = (user: EphemeralUserModel) => {
  if (!user.start_timestamp || !user.end_timestamp) return "—";

  const totalSeconds = user.end_timestamp - user.start_timestamp;

  if (totalSeconds < 0) return "—";

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return minutes <= 0 ? `${seconds}s` : `${minutes}min ${seconds}s`;
};

const _getTimestampDate = (language: string, timestamp?: number, withHour = false) => {
  if (!timestamp) return "—";

  return formatDateHumanly(language, new Date(timestamp * 1000).toISOString(), withHour);
};

const StatBlock = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="off-white-background rounded-3 p-3 h-100">
    <div className="text-muted s-regular">{label}</div>
    <div className="m-regular">{value}</div>
  </div>
);

export const EphemeralUser = () => {
  const { ephemeral_user_id } = useParams();
  const navigate = useNavigate();
  const { language } = useSession();

  const [user, setUser] = useState<EphemeralUserModel | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchEphemeralUserById(ephemeral_user_id!, setUser, setLoaded, language);
  }, [ephemeral_user_id, language]);

  if (!loaded) {
    return (
      <div className="p-3 p-md-4">
        <div className="d-flex align-items-center mb-4">
          <span
            className="dst-icon-chevron-left p-2 medium-gray-background rounded me-3 clickable"
            onClick={() => navigate("/ephemeral/users")}
            role="button"
          />

          <h2 className="mb-0 fw-bold">{translate(language, "PAGES.EPHEMERAL_USER_DETAIL.TITLE")}</h2>
        </div>

        <Card className="border-0 shadow-sm">
          <Card.Body className="text-muted text-center py-5">
            {translate(language, "PAGES.EPHEMERAL_USER_DETAIL.LOADING")}
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-3 p-md-4">
        <div className="d-flex align-items-center mb-4">
          <span
            className="dst-icon-chevron-left p-2 medium-gray-background rounded me-3 clickable"
            onClick={() => navigate("/ephemeral/users")}
            role="button"
          />

          <h2 className="mb-0 fw-bold">{translate(language, "PAGES.EPHEMERAL_USER_DETAIL.TITLE")}</h2>
        </div>

        <div className="alert alert-warning">
          {translate(language, "PAGES.EPHEMERAL_USER_DETAIL.NO_USER_FOUND")} <code>{ephemeral_user_id}</code>
        </div>
      </div>
    );
  }

  const score = _getUserScore(user);
  const statusColor = _getStatusColor(user.succeed);

  return (
    <div className="p-3 p-md-4">
      <Helmet>
        <title>Ephemeral user - {user?.name || user?.ID}</title>
      </Helmet>
      <div className="d-flex align-items-center mb-4">
        <span
          className="dst-icon-chevron-left p-2 medium-gray-background rounded me-3 clickable"
          onClick={() => navigate("/ephemeral/users")}
          role="button"
        />
        <h2 className="mb-0 fw-bold">{translate(language, "PAGES.EPHEMERAL_USER_DETAIL.TITLE")}</h2>
      </div>

      <Card className="border-0 shadow-sm mb-4 overflow-hidden">
        <div
          className={`bg-${statusColor} bg-opacity-10 px-4 py-3 border-bottom border-${statusColor} border-opacity-25`}
        >
          <div className="d-flex align-items-start justify-content-between gap-3">
            <div>
              <h2 className="mb-1 fw-semibold h3">{user.name || "—"}</h2>
              <div className="text-muted s-regular">{user.email || "—"}</div>
            </div>

            <Badge bg={statusColor} className="px-3 py-2 rounded-pill m-regular">
              {user.succeed === true
                ? translate(language, "PAGES.EPHEMERAL_USER_DETAIL.STATUS.PASSED")
                : user.succeed === false
                ? translate(language, "PAGES.EPHEMERAL_USER_DETAIL.STATUS.FAILED")
                : translate(language, "PAGES.EPHEMERAL_USER_DETAIL.STATUS.PENDING")}
            </Badge>
          </div>
        </div>

        <Card.Body className="p-4">
          <div className="d-flex align-items-start gap-3 mb-4 s-regular text-muted flex-wrap">
            <span>
              {translate(language, "GLOBAL.ID")}{" "}
              <code className="text-body off-white-background rounded px-2 py-1">{user.ID}</code>
            </span>

            <span>·</span>

            <span>
              {translate(language, "PAGES.EPHEMERAL_USER_DETAIL.SESSION_LABEL")}{" "}
              {user.session_id ? (
                <a
                  href={`/ephemeral/sessions/${user.session_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                >
                  <code className="text-body off-white-background rounded px-2 py-1">{user.session_id}</code>
                </a>
              ) : (
                <code className="text-body off-white-background rounded px-2 py-1">—</code>
              )}
            </span>
          </div>

          <Row className="g-3 mb-3">
            <Col xs={6} md={4}>
              <StatBlock
                label={translate(language, "PAGES.EPHEMERAL_USER_DETAIL.SCORE")}
                value={
                  <div className="d-flex align-items-center">
                    <span className="me-2">
                      {score.success} / {score.total}
                    </span>

                    <span className="text-muted s-regular off-white-background border rounded-pill px-2 py-1">
                      {score.ratio}%
                    </span>
                  </div>
                }
              />
            </Col>

            <Col xs={6} md={4}>
              <StatBlock label={translate(language, "GLOBAL.DURATION")} value={_getUserDuration(user)} />
            </Col>

            <Col xs={12} md={4}>
              <StatBlock
                label={translate(language, "PAGES.EPHEMERAL_USER_DETAIL.CREATED")}
                value={_getTimestampDate(language, user.dt_created, true)}
              />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xs={6}>
              <StatBlock
                label={translate(language, "PAGES.EPHEMERAL_USER_DETAIL.STARTED")}
                value={_getTimestampDate(language, user.start_timestamp, true)}
              />
            </Col>

            <Col xs={6}>
              <StatBlock
                label={translate(language, "PAGES.EPHEMERAL_USER_DETAIL.ENDED")}
                value={_getTimestampDate(language, user.end_timestamp, true)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="mb-3">
        <h5 className="mb-0 fw-semibold">{translate(language, "PAGES.EPHEMERAL_USER_DETAIL.QUESTIONS")}</h5>
        <p className="text-muted s-regular mb-0">
          {score.total} {translate(language, "PAGES.EPHEMERAL_USER_DETAIL.TOTAL")} · {score.success}{" "}
          {translate(language, "PAGES.EPHEMERAL_USER_DETAIL.CORRECT")} · {score.failed}{" "}
          {translate(language, "PAGES.EPHEMERAL_USER_DETAIL.INCORRECT")}{" "}
        </p>
      </div>

      {!user.answers?.length ? (
        <Card className="border-0 off-white-background mb-4">
          <Card.Body className="text-muted text-center py-5">
            {translate(language, "PAGES.EPHEMERAL_USER_DETAIL.NO_ANSWERS")}
          </Card.Body>
        </Card>
      ) : (
        <Accordion defaultActiveKey="0" className="mb-4" id="ephemeral-user-accordion">
          {(user.answers ?? []).map((answer, index) => {
            const answerStatusColor = _getStatusColor(answer.succeed);

            return (
              <Accordion.Item
                eventKey={index.toString()}
                key={`${user.ID}-${index}`}
                className="mb-2 rounded overflow-hidden"
                style={{
                  borderLeft: `3px solid var(--bs-${answerStatusColor})`,
                }}
              >
                <Accordion.Header>
                  <div className="d-flex align-items-start justify-content-between gap-3 w-100 pe-3 overflow-hidden">
                    <span className="s-regular fw-semibold" style={{ minWidth: "30px" }}>
                      #{index + 1}
                    </span>

                    <span className="text-muted s-regular flex-grow-1">{answer.resume}</span>

                    <Badge bg={answerStatusColor}>
                      {answer.succeed === true
                        ? translate(language, "PAGES.EPHEMERAL_USER_DETAIL.STATUS.PASSED")
                        : answer.succeed === false
                        ? translate(language, "PAGES.EPHEMERAL_USER_DETAIL.STATUS.FAILED")
                        : translate(language, "PAGES.EPHEMERAL_USER_DETAIL.STATUS.PENDING")}
                    </Badge>
                  </div>
                </Accordion.Header>

                <Accordion.Body className="off-white-background">
                  <div className="xs-regular text-muted mb-2">
                    {translate(language, "PAGES.EPHEMERAL_USER_DETAIL.ANSWER")}
                  </div>

                  {(answer.answer ?? []).length > 0 ? (
                    <div className="d-flex flex-column gap-2">
                      {answer.answer?.map((value, answer_index) => (
                        <div
                          key={`${user.ID}-${index}-${answer_index}`}
                          className="white-background border rounded px-3 py-2 s-regular"
                        >
                          {value}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted s-regular">
                      {translate(language, "PAGES.EPHEMERAL_USER_DETAIL.NO_ANSWER")}
                    </span>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}
    </div>
  );
};

export default EphemeralUser;
